import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/authContext.tsx";

const STORAGE_PREFIX = "webadmincc-favorites_";
const EMPTY: string[] = [];
const EVT = "webadmincc-favorites-changed";

function getStorageKey(uid: number | undefined): string | null {
  return uid != null ? `${STORAGE_PREFIX}${uid}` : null;
}

function readFavorites(key: string | null): string[] {
  if (!key) return EMPTY;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : EMPTY;
  } catch {
    return EMPTY;
  }
}

function writeFavorites(key: string, favorites: string[]): void {
  localStorage.setItem(key, JSON.stringify(favorites));
  // Notify same-tab listeners (storage event only fires cross-tab)
  window.dispatchEvent(new Event(EVT));
}

export function useFavorites() {
  const { session } = useAuth();
  const key = getStorageKey(session?.UID);
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites(key));

  // Re-read when key changes or when any tab writes
  useEffect(() => {
    setFavorites(readFavorites(key));

    function onUpdate() {
      setFavorites(readFavorites(key));
    }
    // Same-tab updates
    window.addEventListener(EVT, onUpdate);
    // Cross-tab updates
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener(EVT, onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, [key]);

  const isFavorite = useCallback(
    (pageName: string): boolean =>
      favorites.includes(pageName.toLowerCase()),
    [favorites],
  );

  const toggleFavorite = useCallback(
    (pageName: string) => {
      if (!key) return;
      const lower = pageName.toLowerCase();
      const current = readFavorites(key);
      const next = current.includes(lower)
        ? current.filter((n) => n !== lower)
        : [...current, lower];
      writeFavorites(key, next);
    },
    [key],
  );

  const removeFavorite = useCallback(
    (pageName: string) => {
      if (!key) return;
      const lower = pageName.toLowerCase();
      const current = readFavorites(key);
      writeFavorites(key, current.filter((n) => n !== lower));
    },
    [key],
  );

  const reorderFavorites = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!key) return;
      if (fromIndex === toIndex) return;
      const current = readFavorites(key);
      if (fromIndex < 0 || fromIndex >= current.length) return;
      if (toIndex < 0 || toIndex > current.length) return;
      const next = current.slice();
      const [moved] = next.splice(fromIndex, 1);
      if (moved === undefined) return;
      const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
      next.splice(insertAt, 0, moved);
      writeFavorites(key, next);
    },
    [key],
  );

  return { favorites, isFavorite, toggleFavorite, removeFavorite, reorderFavorites };
}
